import { useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react'
import { DropDownList, DropDownListFilterChangeEvent } from '@progress/kendo-react-dropdowns'
import { NumericTextBox, TextArea } from '@progress/kendo-react-inputs'
import { FaMagic, FaPlus } from 'react-icons/fa'
import { dropDown } from '../../../../data/interfaces'
import { getJobsForInvoiceDropdownByClient, getTargetJob } from '../../../../data/jobData'
import { getInvoiceTemplateData, improveDescriptionWithAI } from '../../../../data/invoiceTemplateData'
import { getInvoiceItemDescriptionData } from '../../../../data/invoiceItemDescription'
import { getXeroDepartments, getXeroTaxRatesWithRates, XeroTaxRateWithRate } from '../../../../data/invoiceItemData'
import { getXeroDepartments as getXeroDepartmentsFull } from '../../../../data/xeroData'
import { useToast } from '../../../../contexts/ToastContext'

/** A line being entered on a new invoice, before anything is saved. */
export interface InvoiceLineDraft {
  key: string
  /** Set for a line already saved on the invoice. */
  invoiceitemId: number | null
  /** How to show the line's job when it's no longer an open job for the client. */
  jobLabel?: string
  jobId: number | null
  invoiceTemplateId: number | null
  templateName: string
  description: string
  orderNo: string | null
  itemValue: number
  xeroDepartmentId: number | null
  xeroTaxRateId: number | null
}

// A "Less first hour invoiced" line takes the first hour back off, so it
// always counts as a deduction however the amount was typed.
const LESS_FIRST_HOUR_TEMPLATE = 'Less first hour invoiced'

export const lineAmount = (line: InvoiceLineDraft) =>
  line.templateName === LESS_FIRST_HOUR_TEMPLATE ? -Math.abs(line.itemValue || 0) : line.itemValue || 0

let nextKey = 0
export const newInvoiceLine = (copyFrom?: InvoiceLineDraft): InvoiceLineDraft => ({
  key: `line-${nextKey++}`,
  invoiceitemId: null,
  jobId: null,
  invoiceTemplateId: null,
  templateName: '',
  description: '',
  orderNo: null,
  itemValue: 0,
  xeroDepartmentId: null,
  // Most invoices use one tax rate throughout, so a new line starts with the
  // one above it, as Xero does.
  xeroTaxRateId: copyFrom?.xeroTaxRateId ?? null,
})

/** What still needs filling in on a line, or an empty list when it's complete. */
export const lineProblems = (line: InvoiceLineDraft): string[] => {
  const problems: string[] = []
  if (!line.description.trim()) problems.push('description')
  if (!line.xeroDepartmentId) problems.push('Xero department')
  if (!line.xeroTaxRateId) problems.push('tax rate')
  return problems
}

interface InvoiceLinesEditorProps {
  clientId: number
  lines: InvoiceLineDraft[]
  setLines: Dispatch<SetStateAction<InvoiceLineDraft[]>>
  /** Set once the user has tried to save, so missing fields are highlighted. */
  showErrors: boolean
  readOnly?: boolean
  /** Told the running totals, so the page header can show them too. */
  onTotals?: (totals: InvoiceTotals) => void
}

export interface InvoiceTotals {
  subtotal: number
  vat: number
  total: number
}

export const money = (value: number) =>
  value.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })

const InvoiceLinesEditor = ({ clientId, lines, setLines, showErrors, readOnly = false, onTotals }: InvoiceLinesEditorProps) => {
  const { showToast } = useToast()
  const [jobs, setJobs] = useState<dropDown[]>([])
  const [jobsState, setJobsState] = useState<'idle' | 'loading' | 'loaded' | 'failed'>('idle')
  // Each line searches the job list separately, and the search is cleared
  // whenever the list opens or closes. One shared search used to leave the next
  // line's list filtered by whatever was typed on the last one.
  const [jobFilters, setJobFilters] = useState<Record<string, string>>({})
  const [templates, setTemplates] = useState<dropDown[]>([])
  const [departments, setDepartments] = useState<dropDown[]>([])
  const [departmentsFull, setDepartmentsFull] = useState<any[]>([])
  const [taxRates, setTaxRates] = useState<XeroTaxRateWithRate[]>([])
  const [busyLines, setBusyLines] = useState<Record<string, string>>({})

  useEffect(() => {
    getInvoiceTemplateData().then(setTemplates).catch(() => setTemplates([]))
    getXeroDepartments()
      .then((res) => setDepartments(res.map((d: any) => ({ text: d.text, value: Number(d.value) }))))
      .catch(() => setDepartments([]))
    getXeroDepartmentsFull().then(setDepartmentsFull).catch(() => setDepartmentsFull([]))
    getXeroTaxRatesWithRates().then(setTaxRates).catch(() => setTaxRates([]))
  }, [])

  // Jobs belong to the client, so changing the client clears any job already
  // picked - it would belong to someone else. Not when the client is first set,
  // which is an existing invoice loading with its lines.
  const previousClientId = useRef(clientId)
  useEffect(() => {
    if (previousClientId.current && previousClientId.current !== clientId) {
      setLines((prev) => prev.map((l) => ({ ...l, jobId: null, jobLabel: undefined, orderNo: null })))
    }
    previousClientId.current = clientId
    if (!clientId) {
      setJobs([])
      setJobsState('idle')
      return
    }
    let cancelled = false
    setJobsState('loading')
    getJobsForInvoiceDropdownByClient(clientId)
      .then((res) => {
        if (cancelled) return
        setJobs(res.map((j: any) => ({ text: j.text, value: Number(j.value) })))
        setJobsState('loaded')
      })
      .catch(() => {
        if (cancelled) return
        setJobs([])
        setJobsState('failed')
        showToast("Couldn't load this client's jobs. Try choosing the client again.", 'error')
      })
    return () => {
      cancelled = true
    }
  }, [clientId])

  const updateLine = (key: string, patch: Partial<InvoiceLineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))

  const setBusy = (key: string, what: string | null) =>
    setBusyLines((prev) => {
      const next = { ...prev }
      if (what) next[key] = what
      else delete next[key]
      return next
    })

  // A template and a job together give the line its standard wording.
  const fillDescription = async (key: string, templateId: number | null, jobId: number | null) => {
    if (!templateId || !jobId) return
    setBusy(key, 'Filling in description…')
    try {
      const description = await getInvoiceItemDescriptionData(templateId.toString(), jobId.toString())
      updateLine(key, { description: description ?? '' })
    } catch {
      showToast("Couldn't fill in the description from the template", 'error')
    } finally {
      setBusy(key, null)
    }
  }

  const handleJobChange = async (line: InvoiceLineDraft, jobId: number | null) => {
    updateLine(line.key, { jobId, orderNo: null })
    if (!jobId) return

    try {
      const job = await getTargetJob(jobId)
      const worktypeId = job?.worktype?.worktypeId
      const department = departmentsFull.find((d) => (d.worktypeId || d.WorktypeId) === worktypeId)
      updateLine(line.key, {
        orderNo: job?.refFormatted ?? null,
        // The job's worktype decides the Xero department, as when lines are
        // added one at a time - still changeable if it's wrong.
        ...(department?.xeroTrackingDepartmentId ? { xeroDepartmentId: Number(department.xeroTrackingDepartmentId) } : {}),
      })
    } catch {
      // The job's details only pre-fill the line; it can still be completed by hand.
    }

    fillDescription(line.key, line.invoiceTemplateId, jobId)
  }

  const handleTemplateChange = (line: InvoiceLineDraft, template: dropDown | null) => {
    const templateId = template?.value ? Number(template.value) : null
    updateLine(line.key, { invoiceTemplateId: templateId, templateName: template?.text ?? '' })
    fillDescription(line.key, templateId, line.jobId)
  }

  const handleImprove = async (line: InvoiceLineDraft) => {
    if (!line.description.trim() || !line.invoiceTemplateId || !line.jobId) {
      showToast('Choose a job and a template, and enter a description first', 'error')
      return
    }
    setBusy(line.key, 'Improving…')
    try {
      const improved = await improveDescriptionWithAI(line.invoiceTemplateId, line.jobId, line.description)
      updateLine(line.key, { description: improved })
    } catch (error: any) {
      showToast(error.message || 'Failed to improve text', 'error')
    } finally {
      setBusy(line.key, null)
    }
  }

  const totals = useMemo(() => {
    let subtotal = 0
    let vat = 0
    lines.forEach((line) => {
      const amount = lineAmount(line)
      const rate = taxRates.find((t) => t.value === line.xeroTaxRateId)?.rate ?? 0
      subtotal += amount
      vat += (amount * rate) / 100
    })
    vat = Math.round(vat * 100) / 100
    return { subtotal, vat, total: subtotal + vat }
  }, [lines, taxRates])

  useEffect(() => {
    onTotals?.(totals)
  }, [totals])

  // A saved line can be for a job that's since been completed, which the open
  // jobs list no longer has; keep it choosable so the line still shows it.
  const allJobs = useMemo(() => {
    const extra = lines
      .filter((l) => l.jobId && !jobs.some((j) => j.value === l.jobId))
      .map((l) => ({ text: l.jobLabel ?? `Job ${l.jobId}`, value: l.jobId as number }))
      .filter((j, i, list) => list.findIndex((o) => o.value === j.value) === i)
    return [...jobs, ...extra]
  }, [jobs, lines])

  const jobsFor = (key: string) => {
    const filter = (jobFilters[key] ?? '').toLowerCase()
    return filter ? allJobs.filter((j) => j.text.toLowerCase().includes(filter)) : allJobs
  }

  const setJobFilter = (key: string, value: string) =>
    setJobFilters((prev) => ({ ...prev, [key]: value }))

  // What the job list says when it has nothing to offer.
  const noJobsText = !clientId
    ? 'Choose a client first'
    : jobsState === 'loading'
      ? 'Loading jobs…'
      : jobsState === 'failed'
        ? "Couldn't load jobs"
        : jobs.length === 0
          ? 'This client has no open jobs'
          : 'No job'

  const invalid = (missing: boolean) => (showErrors && missing ? ' inv-invalid' : '')

  return (
    <div>
      <div className='inv-grid'>
        <div className='inv-grid__row inv-grid__head'>
          <span>Job</span>
          <span>Template</span>
          <span>Description</span>
          <span>Department</span>
          <span>Tax rate</span>
          <span className='inv-grid__num'>Amount GBP</span>
          <span />
        </div>

        {lines.map((line) => {
          const busy = busyLines[line.key]
          const canImprove = !busy && !!line.description.trim()
          return (
            <div key={line.key} className='inv-grid__row'>
              <div className='inv-grid__cell'>
                <DropDownList
                  data={jobsFor(line.key)}
                  textField='text'
                  dataItemKey='value'
                  filterable={true}
                  filter={jobFilters[line.key] ?? ''}
                  onFilterChange={(e: DropDownListFilterChangeEvent) => setJobFilter(line.key, e.filter.value ?? '')}
                  onOpen={() => setJobFilter(line.key, '')}
                  onClose={() => setJobFilter(line.key, '')}
                  value={allJobs.find((j) => j.value === line.jobId) ?? null}
                  defaultItem={{ text: noJobsText, value: null }}
                  disabled={readOnly || !clientId}
                  onChange={(e) => handleJobChange(line, e.value?.value ? Number(e.value.value) : null)}
                />
                {line.orderNo && <span className='inv-grid__hint'>Ref {line.orderNo}</span>}
              </div>

              <div className='inv-grid__cell'>
                <DropDownList
                  data={templates}
                  textField='text'
                  dataItemKey='value'
                  value={templates.find((t) => Number(t.value) === line.invoiceTemplateId) ?? null}
                  defaultItem={{ text: 'No template', value: null }}
                  disabled={readOnly}
                  onChange={(e) => handleTemplateChange(line, e.value?.value ? e.value : null)}
                />
              </div>

              <div className={'inv-grid__cell' + invalid(!line.description.trim())}>
                <TextArea
                  value={line.description}
                  rows={2}
                  autoSize={true}
                  placeholder='What is being invoiced'
                  disabled={readOnly || !!busy}
                  onChange={(e) => updateLine(line.key, { description: String(e.value ?? '') })}
                />
                {busy && <span className='inv-grid__hint'>{busy}</span>}
                {!readOnly && !busy && (
                  <button
                    type='button'
                    className='inv-grid__ai'
                    onClick={() => handleImprove(line)}
                    disabled={!canImprove}
                    title='Improve description with AI'
                  >
                    <FaMagic /> Improve with AI
                  </button>
                )}
              </div>

              <div className={'inv-grid__cell' + invalid(!line.xeroDepartmentId)}>
                <DropDownList
                  data={departments}
                  textField='text'
                  dataItemKey='value'
                  value={departments.find((d) => Number(d.value) === line.xeroDepartmentId) ?? null}
                  defaultItem={{ text: 'Choose…', value: null }}
                  disabled={readOnly}
                  onChange={(e) => updateLine(line.key, { xeroDepartmentId: e.value?.value ? Number(e.value.value) : null })}
                />
              </div>

              <div className={'inv-grid__cell' + invalid(!line.xeroTaxRateId)}>
                <DropDownList
                  data={taxRates}
                  textField='text'
                  dataItemKey='value'
                  value={taxRates.find((t) => t.value === line.xeroTaxRateId) ?? null}
                  defaultItem={{ text: 'Choose…', value: null }}
                  disabled={readOnly}
                  onChange={(e) => updateLine(line.key, { xeroTaxRateId: e.value?.value ?? null })}
                />
              </div>

              <div className='inv-grid__cell inv-grid__num'>
                <NumericTextBox
                  value={line.itemValue}
                  format='n2'
                  spinners={false}
                  disabled={readOnly}
                  onChange={(e) => updateLine(line.key, { itemValue: e.value ?? 0 })}
                />
                {line.templateName === LESS_FIRST_HOUR_TEMPLATE && (
                  <span className='inv-grid__hint inv-grid__hint--right'>Deducted: {money(lineAmount(line))}</span>
                )}
              </div>

              {readOnly ? (
                <span />
              ) : (
                <button
                  type='button'
                  className='inv-grid__remove'
                  title={lines.length === 1 ? 'An invoice needs at least one line' : 'Remove line'}
                  aria-label='Remove line'
                  onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                  disabled={lines.length === 1}
                >
                  ×
                </button>
              )}
            </div>
          )
        })}
      </div>

      <div className='inv-under'>
        {readOnly ? (
          <span />
        ) : (
          <button
            type='button'
            className='inv-add-line'
            onClick={() => setLines((prev) => [...prev, newInvoiceLine(prev[prev.length - 1])])}
          >
            <FaPlus /> Add a new line
          </button>
        )}

        <div className='inv-totals'>
          <div className='inv-totals__row'>
            <span>Subtotal</span>
            <span>{money(totals.subtotal)}</span>
          </div>
          <div className='inv-totals__row'>
            <span>Total VAT</span>
            <span>{money(totals.vat)}</span>
          </div>
          <div className='inv-totals__row inv-totals__row--total'>
            <span>Total</span>
            <span>{money(totals.total)}</span>
          </div>
          <div className='inv-totals__note'>Xero confirms the final VAT when the invoice is sent.</div>
        </div>
      </div>
    </div>
  )
}

export default InvoiceLinesEditor
